
import React, { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { Shuffle } from "lucide-react";

interface PartnerLogo {
  id: string;
  name: string;
  imageUrl: string;
}

interface PartnerLogosProps {
  fallbackToStaticLogos?: boolean;
}

// Helper function to shuffle an array (Fisher-Yates algorithm)
const shuffleArray = <T extends unknown>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

export const PartnerLogos: React.FC<PartnerLogosProps> = ({ 
  fallbackToStaticLogos = true
}) => {
  const [logos, setLogos] = useState<PartnerLogo[]>([]);
  const [loading, setLoading] = useState(true);

  // Predefined static logos as fallback
  const staticLogos: PartnerLogo[] = [
    { id: '1', name: 'Pathé', imageUrl: '/images/pathe-films-logo.jpg' },
    { id: '2', name: 'StudioCanal', imageUrl: '/images/studiocanal-logo.jpg' },
    { id: '3', name: 'Cours Florent', imageUrl: '/images/cours-florent-logo.png' },
    { id: '4', name: 'Intervista Law', imageUrl: '/images/intervista-law-logo.jpeg' },
    { id: '5', name: 'Ad Astra Films', imageUrl: '/images/ad-astra-logo.png' },
    { id: '6', name: 'Tandem', imageUrl: '/images/tandem-logo.png' },
  ];

  useEffect(() => {
    const fetchPartnerLogos = async () => {
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .storage
          .from('partner-logos')
          .list();
        
        if (error) {
          console.error('Error fetching partner logos:', error);
          throw error;
        }

        if (data && data.length > 0) {
          const logoData = data.map(file => {
            const publicUrl = supabase.storage
              .from('partner-logos')
              .getPublicUrl(file.name).data.publicUrl;
              
            const baseName = file.name.split('.')[0].replace(/_/g, ' ');
            
            return {
              id: file.id || file.name,
              name: baseName,
              imageUrl: publicUrl
            };
          });
          
          // Shuffle the logos to display them in random order
          const shuffledLogoData = shuffleArray(logoData);
          
          if (shuffledLogoData.length > 0) {
            setLogos(shuffledLogoData);
          } else if (fallbackToStaticLogos) {
            // Shuffle the static logos as well
            setLogos(shuffleArray(staticLogos));
          }
        } else if (fallbackToStaticLogos) {
          // Shuffle the static logos
          setLogos(shuffleArray(staticLogos));
        }
      } catch (err) {
        console.error('Failed to load partner logos:', err);
        
        if (fallbackToStaticLogos) {
          // Shuffle the static logos in case of error
          setLogos(shuffleArray(staticLogos));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPartnerLogos();
  }, [fallbackToStaticLogos]);

  if (loading) {
    return null;
  }

  // Ensure we only display 6 logos
  const displayLogos = logos.slice(0, 6);

  return (
    <div className="grid grid-cols-3 gap-8 max-w-5xl mx-auto px-4 my-12">
      {displayLogos.map((logo, index) => (
        <div key={logo.id} className={`flex items-center justify-center ${index % 2 === 0 ? 'mb-8' : 'mt-8'}`}>
          <img 
            src={logo.imageUrl} 
            alt={`${logo.name}`} 
            className="max-h-24 sm:max-h-36 md:max-h-48 lg:max-h-64 w-auto object-contain"
            loading="lazy"
            onError={(e) => {
              console.error(`Error loading logo: ${logo.imageUrl}`);
              e.currentTarget.src = '/placeholder.svg';
            }}
          />
        </div>
      ))}
    </div>
  );
};
